import { Injectable } from '@angular/core';
import { user_autentica, users } from '../models/Login';
import { QR_Personalizado, QR_Estilo } from '../models/QR';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  /** Variantes del rol administrativo aceptadas (ver esAdministrador). */
  private static readonly ROLES_ADMIN = ['admin', 'administrador'];

  private readonly QR_PERSONALIZADOS_PREFIX = 'qr-personalizados-';
  private readonly QR_PERSONALIZADOS_MAX = 20; // evita que localStorage crezca sin límite (incluye thumbnails en base64)
  private readonly QR_ESTILOS_REGISTRO_KEY = 'qr-estilos-registrados';

  constructor() { }

  /**
   * Recuerda solo el correo del usuario (para precargar el campo "usuario").
   * No se persiste la contraseña por motivos de seguridad: cualquier acceso
   * al localStorage (XSS, dispositivo compartido, etc.) expondría credenciales
   * en texto plano.
   */
  setUsuarioRecordarStorage(user: user_autentica): void {
    const soloEmail: user_autentica = { email: user.email, psw: undefined };
    localStorage.setItem('datos-usuario_recordar', JSON.stringify(soloEmail));
  }
  getUsuarioRecordarStorage(): any {
    const item = localStorage.getItem('datos-usuario_recordar');
    return item ? JSON.parse(item) : null;
  }


  /**
   * Guarda los datos de sesión del usuario. La contraseña se elimina antes de
   * persistir: nunca debe llegar a localStorage en texto plano.
   */
  setUsuarioStorage(usuario: users): void {
    const { password, ...usuarioSinPassword } = usuario;
    localStorage.setItem('datos-usuario', JSON.stringify(usuarioSinPassword));
  }

  getUsuarioStorage(): any {
    const item = localStorage.getItem('datos-usuario');
    return item ? JSON.parse(item) : null;
  }

  removeUsuarioStorage(): void {
    localStorage.removeItem('datos-usuario');
    this.removeToken();
  }

  /**
   * Único punto de verdad para "¿es administrador?".
   *
   * El rol está escrito de forma inconsistente en la BD ('administrador',
   * 'admin', 'Usuario'), y hasta ahora cada pantalla lo comparaba por su
   * cuenta contra el literal exacto 'administrador'. Eso dejaba a las cuentas
   * con rol 'admin' sin permisos y, en "Registrados", pidiendo solo sus
   * propios QR en vez de todos. Se normaliza el valor para que la forma en
   * que esté escrito el dato no vuelva a romper la aplicación.
   */
  esAdministrador(): boolean {
    const rol = this.getUsuarioStorage()?.rol;
    return StorageService.ROLES_ADMIN.includes((rol ?? '').trim().toLowerCase());
  }

  removeUsuarioStorage_Recordar(): void {
    localStorage.removeItem('datos-usuario_recordar');
  }

  //---------------------------------------------------------------------
  // Token de sesión (recibido del login), separado de los datos de usuario
  // para que el HttpInterceptor pueda adjuntarlo a los requests salientes.
  //---------------------------------------------------------------------
  setToken(token: string): void {
    localStorage.setItem('qr-token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('qr-token');
  }

  removeToken(): void {
    localStorage.removeItem('qr-token');
  }

  //---------------------------------------------------------------------
  // QR personalizados guardados por el usuario (color + ícono central).
  // Se guardan solo en este navegador, con clave por usuario para que un
  // dispositivo compartido no mezcle los QR de distintas cuentas.
  //---------------------------------------------------------------------
  private getQRPersonalizadosKey(): string {
    const idUser = this.getUsuarioStorage()?.idUser ?? 'anonimo';
    return `${this.QR_PERSONALIZADOS_PREFIX}${idUser}`;
  }

  getQRsPersonalizados(): QR_Personalizado[] {
    const item = localStorage.getItem(this.getQRPersonalizadosKey());
    return item ? JSON.parse(item) : [];
  }

  guardarQRPersonalizado(qr: QR_Personalizado): QR_Personalizado[] {
    const lista = this.getQRsPersonalizados();
    lista.unshift(qr);
    if (lista.length > this.QR_PERSONALIZADOS_MAX) {
      lista.length = this.QR_PERSONALIZADOS_MAX;
    }
    localStorage.setItem(this.getQRPersonalizadosKey(), JSON.stringify(lista));
    return lista;
  }

  eliminarQRPersonalizado(id: string): QR_Personalizado[] {
    const lista = this.getQRsPersonalizados().filter(qr => qr.id !== id);
    localStorage.setItem(this.getQRPersonalizadosKey(), JSON.stringify(lista));
    return lista;
  }

  //---------------------------------------------------------------------
  // Personalización (color + ícono) de QR ya registrados, usada en la
  // pantalla "Registrados". Se indexa por id_qr (no por usuario): es el
  // estilo visual de ese código, no una preferencia personal de quien mira.
  //---------------------------------------------------------------------
  private getEstilosRegistradosMap(): { [id_qr: number]: QR_Estilo } {
    const item = localStorage.getItem(this.QR_ESTILOS_REGISTRO_KEY);
    return item ? JSON.parse(item) : {};
  }

  getEstiloQRRegistrado(id_qr: number): QR_Estilo | undefined {
    return this.getEstilosRegistradosMap()[id_qr];
  }

  guardarEstiloQRRegistrado(id_qr: number, estilo: QR_Estilo): void {
    const mapa = this.getEstilosRegistradosMap();
    mapa[id_qr] = estilo;
    localStorage.setItem(this.QR_ESTILOS_REGISTRO_KEY, JSON.stringify(mapa));
  }
}
